import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  Compass, 
  CreateCompassRequest, 
  UpdateCompassRequest, 
  ApiResponse, 
  PaginatedResponse,
  CompassPermission,
  ShareCompassRequest,
  BulkShareCompassRequest,
  CompassNote,
  CreateNoteRequest,
  UpdateNoteRequest,
  UserNotification,
  NotificationCounts
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.VITE_API_URL || 'https://accountability-compass-backend.calmwave-fb14aad8.centralus.azurecontainerapps.io',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async loginWithGoogle(googleToken: string): Promise<{ user: User; token: string }> {
    const response: AxiosResponse<ApiResponse<{ user: User; token: string }>> = 
      await this.api.post('/api/auth/google', { token: googleToken });
    return response.data.data!;
  }

  async loginWithMicrosoft(microsoftToken: string): Promise<{ user: User; token: string }> {
    const response: AxiosResponse<ApiResponse<{ user: User; token: string }>> = 
      await this.api.post('/api/auth/microsoft', { token: microsoftToken });
    return response.data.data!;
  }

  async verifyToken(): Promise<{ valid: boolean; user?: User }> {
    const response: AxiosResponse<ApiResponse<{ valid: boolean; user?: User }>> = 
      await this.api.post('/api/auth/verify');
    return response.data.data!;
  }

  async getProfile(): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = 
      await this.api.get('/api/auth/profile');
    return response.data.data!.user;
  }

  async refreshToken(): Promise<string> {
    const response: AxiosResponse<ApiResponse<{ token: string }>> = 
      await this.api.post('/api/auth/refresh');
    return response.data.data!.token;
  }

  async logout(): Promise<void> {
    await this.api.post('/api/auth/logout');
  }

  // Compass methods
  async getCompasses(page: number = 1, limit: number = 10): Promise<PaginatedResponse<Compass>> {
    const response: AxiosResponse<PaginatedResponse<Compass>> = 
      await this.api.get(`/api/compasses?page=${page}&limit=${limit}`);
    return response.data;
  }

  async getCompass(id: string): Promise<Compass> {
    const response: AxiosResponse<ApiResponse<Compass>> = 
      await this.api.get(`/api/compasses/${id}`);
    return response.data.data!;
  }

  async createCompass(compassData: CreateCompassRequest): Promise<Compass> {
    const response: AxiosResponse<ApiResponse<Compass>> = 
      await this.api.post('/api/compasses', compassData);
    return response.data.data!;
  }

  async updateCompass(id: string, updates: UpdateCompassRequest): Promise<Compass> {
    const response: AxiosResponse<ApiResponse<Compass>> = 
      await this.api.put(`/api/compasses/${id}`, updates);
    return response.data.data!;
  }

  async deleteCompass(id: string): Promise<void> {
    await this.api.delete(`/api/compasses/${id}`);
  }

  async getDraftCompasses(): Promise<Compass[]> {
    const response: AxiosResponse<ApiResponse<Compass[]>> = 
      await this.api.get('/api/compasses/drafts');
    return response.data.data!;
  }

  async getSubmittedCompasses(page: number = 1, limit: number = 10): Promise<PaginatedResponse<Compass>> {
    const response: AxiosResponse<PaginatedResponse<Compass>> = 
      await this.api.get(`/api/compasses/submitted?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string; service: string }> {
    const response: AxiosResponse<{ status: string; timestamp: string; service: string }> = 
      await this.api.get('/health');
    return response.data;
  }

  // Phase 3: Compass Sharing methods
  async shareCompass(compassId: string, shareData: ShareCompassRequest): Promise<CompassPermission[]> {
    const response: AxiosResponse<ApiResponse<CompassPermission[]>> = 
      await this.api.post(`/api/compass/${compassId}/share`, shareData);
    return response.data.data!;
  }

  async bulkShareCompasses(shareData: BulkShareCompassRequest): Promise<{ shared: number; errors: any[] }> {
    const response: AxiosResponse<ApiResponse<{ shared: number; errors: any[] }>> = 
      await this.api.post('/api/compass/share', shareData);
    return response.data.data!;
  }

  async revokeCompassAccess(compassId: string, userId: string): Promise<void> {
    await this.api.delete(`/api/compass/${compassId}/share/${userId}`);
  }

  async getCompassPermissions(compassId: string): Promise<CompassPermission[]> {
    const response: AxiosResponse<ApiResponse<CompassPermission[]>> = 
      await this.api.get(`/api/compass/${compassId}/permissions`);
    return response.data.data!;
  }

  async getSharedCompasses(): Promise<Compass[]> {
    const response: AxiosResponse<ApiResponse<Compass[]>> = 
      await this.api.get('/api/compass/shared-with-me');
    return response.data.data!;
  }

  // Phase 3: Notes methods
  async addCompassNote(compassId: string, noteData: CreateNoteRequest): Promise<CompassNote> {
    const response: AxiosResponse<ApiResponse<CompassNote>> = 
      await this.api.post(`/api/compass/${compassId}/notes`, noteData);
    return response.data.data!;
  }

  async getCompassNotes(compassId: string): Promise<CompassNote[]> {
    const response: AxiosResponse<ApiResponse<CompassNote[]>> = 
      await this.api.get(`/api/compass/${compassId}/notes`);
    return response.data.data!;
  }

  async getNote(noteId: string): Promise<CompassNote> {
    const response: AxiosResponse<ApiResponse<CompassNote>> = 
      await this.api.get(`/api/compass/notes/${noteId}`);
    return response.data.data!;
  }

  async updateNote(noteId: string, updates: UpdateNoteRequest): Promise<CompassNote> {
    const response: AxiosResponse<ApiResponse<CompassNote>> = 
      await this.api.put(`/api/compass/notes/${noteId}`, updates);
    return response.data.data!;
  }

  async deleteNote(noteId: string): Promise<void> {
    await this.api.delete(`/api/compass/notes/${noteId}`);
  }

  // Phase 3: Notifications methods
  async getNotifications(): Promise<UserNotification[]> {
    const response: AxiosResponse<ApiResponse<UserNotification[]>> = 
      await this.api.get('/api/notifications');
    return response.data.data!;
  }

  async getUnreadNotificationCount(): Promise<NotificationCounts> {
    const response: AxiosResponse<ApiResponse<NotificationCounts>> = 
      await this.api.get('/api/notifications/unread-count');
    return response.data.data!;
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    await this.api.put(`/api/notifications/${notificationId}/read`);
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await this.api.put('/api/notifications/mark-all-read');
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await this.api.delete(`/api/notifications/${notificationId}`);
  }

  // Phase 3: Account Linking methods
  async getLinkedAccounts(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = 
      await this.api.get('/api/account-linking');
    return response.data.data!;
  }

  async linkAccount(email: string): Promise<void> {
    await this.api.post('/api/account-linking/link', { email });
  }

  async unlinkAccount(accountId: string): Promise<void> {
    await this.api.delete(`/api/account-linking/${accountId}`);
  }

  async sendVerificationEmail(accountId: string): Promise<void> {
    await this.api.post(`/api/account-linking/${accountId}/send-verification`);
  }

  async verifyEmail(token: string): Promise<void> {
    await this.api.post('/api/account-linking/verify', { token });
  }

  // User preferences methods
  async put(url: string, data: any): Promise<any> {
    const response = await this.api.put(url, data);
    return response.data;
  }

  // Image upload methods
  async uploadCompassImage(compassId: string, quadrant: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('quadrant', quadrant);

    const response: AxiosResponse<ApiResponse<{ imageUrl: string }>> = 
      await this.api.post(`/api/compasses/${compassId}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    return response.data.data!.imageUrl;
  }

  async deleteCompassImage(compassId: string, quadrant: string): Promise<void> {
    await this.api.delete(`/api/compasses/${compassId}/image/${quadrant}`);
  }
}

export const apiService = new ApiService();

// Export individual methods for convenience
export const {
  loginWithGoogle,
  loginWithMicrosoft,
  verifyToken,
  getProfile,
  refreshToken,
  logout,
  getCompasses,
  getCompass,
  createCompass,
  updateCompass,
  deleteCompass,
  getDraftCompasses,
  getSubmittedCompasses,
  healthCheck,
  shareCompass,
  bulkShareCompasses,
  revokeCompassAccess,
  getCompassPermissions,
  getSharedCompasses,
  addCompassNote,
  getCompassNotes,
  getNote,
  updateNote,
  deleteNote,
  getNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  getLinkedAccounts,
  linkAccount,
  unlinkAccount,
  sendVerificationEmail,
  verifyEmail
} = apiService;

export default apiService;
